import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import {
  GET_TICKET_ENDPOINT_COLUMNS,
  GET_TICKET_ENDPOINT_COLUMNS_VALUES,
  GET_TICKET_ENDPOINT_KEYS,
  SAVE_MAPPING,
} from "../TicketingAPI";
import {
  useMutation,
  useQuery,
  useLazyQuery,
  type ApolloClient,
  type DocumentNode,
} from "@apollo/client";
import useSections, {
  type NormalizedField,
  type RawSection,
} from "../useSections/useSections";
import {
  Controller,
  useForm,
  FormProvider,
  useWatch,
  type ControllerRenderProps,
  type ControllerFieldState,
  type FieldValues,
} from "react-hook-form";
import GenericField from "../GenericField/GenericField";
import {
  Loader,
  useIcon,
  TextButton,
  ModalButtonsContainer,
  REQUIRED_ERROR_MESSAGE,
} from "@SeemplicityDev/shared";
import "./TicketEndpointColumns.scss";
import useTicketEndpointMutationData from "../useTicketEndpointMutationData/useTicketEndpointMutationData";
import { systemOptionsEnum, getDataSourceIcon } from "../TicketingUtils";

export const ticketEndpointColumnsModesEnum = {
  CREATE: "create",
  EDIT: "edit",
} as const;

export type TicketEndpointColumnsMode =
  (typeof ticketEndpointColumnsModesEnum)[keyof typeof ticketEndpointColumnsModesEnum];

/**
 * A single mapping-key option (e.g. an issue type / project). Returned from the
 * key field's props by `getPropsFromKeyField`.
 */
interface KeyFieldOption {
  id: string;
  name: string;
}

/**
 * Props for a key field, as returned by `getPropsFromKeyField`. Only `options`
 * is read here. Returned loosely (`object`) by consumers, so it is narrowed at
 * the usage site.
 */
interface KeyFieldProps {
  options?: KeyFieldOption[];
}

/**
 * A normalized field produced by `useSections` (still JS, untyped). Only the
 * props read by `getField` are typed here; values stay narrow enough to remain
 * assignable to `GenericFieldProps`.
 */
interface SectionField {
  name?: string;
  description?: ReactNode;
  size?: string;
  mapping?: { columns?: unknown; rows?: { field_from?: unknown }[] };
  query?: { params?: unknown };
  creatable?: boolean;
  input?: string;
  label?: ReactNode;
  options?: { id?: string; name?: string; disabled?: boolean }[];
  required?: boolean;
  defaultValue?: { html: string; text: string };
}

/** Result payload passed to the create-mapping callbacks. */
interface CreateMappingResult {
  message: string;
}

export interface TicketEndpointColumnsProps {
  keysObject: Record<string, string>;
  ticketProviderId?: string;
  onClose?: () => void;
  ticketManagerId?: string;
  getPropsFromKeyField?: (key: string) => KeyFieldProps | object | undefined;
  ticketManagerFriendlyName?: string;
  ticketManagerMustHaveColumnsMapping?: boolean;
  mode?: TicketEndpointColumnsMode;
  onEventTriggered?: (event: string) => void;
  onCreateMapping?: (result: CreateMappingResult) => void;
  onCreateMappingError?: (result: CreateMappingResult) => void;
  systemLabel?: string;
  client?: ApolloClient<object> | null;
}

const TicketEndpointColumns: React.FC<TicketEndpointColumnsProps> = ({
  keysObject,
  ticketProviderId,
  onClose = () => {},
  ticketManagerId,
  getPropsFromKeyField = () => undefined,
  ticketManagerFriendlyName,
  ticketManagerMustHaveColumnsMapping,
  mode,
  onEventTriggered = () => {},
  onCreateMapping = () => {},
  onCreateMappingError = () => {},
  systemLabel = systemOptionsEnum.SEEMPLICITY,
  client = null,
}) => {
  const [columnsSections, setColumnsSections] = useState<unknown>(null);
  const [columnsValuesSections, setColumnsValuesSections] =
    useState<unknown>(null);
  // `useSections` (JS) assigns into `.current[index]`; entries are section
  // refs exposing `toggleCollapse`. Typed loosely to match the untyped hook.
  const columnsRef = useRef<{ toggleCollapse?: (open: boolean) => void }[]>([]);
  const columnsValuesRef = useRef<
    { toggleCollapse?: (open: boolean) => void }[]
  >([]);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const { getSvg } = useIcon();
  // Widened to FieldValues so the dynamic Controller names (`columns.<field>`)
  // and per-section default values (e.g. `{ html, text }`) are accepted.
  const methods = useForm<FieldValues>({
    mode: "all",
    defaultValues: {
      columns: {},
      columnsValues: {},
    },
  });
  const {
    control,
    handleSubmit,
    formState,
    reset: formReset,
    getValues,
    setValue,
  } = methods;
  const { errors, isValid, isDirty } = formState;

  const {
    getMutationArray: getColumnsValuesMutationArray,
    setGetPropsFromField: setGetPropsFromColumnsValues,
  } = useTicketEndpointMutationData();
  const {
    getMutationArray: getColumnsMutationArray,
    setGetPropsFromField: setGetPropsFromColumns,
  } = useTicketEndpointMutationData();

  const selectedColumns = useWatch({ control, name: "columns" });
  const selectedColumnsValues = useWatch({ control, name: "columnsValues" });

  const [saveMapping] = useMutation(SAVE_MAPPING, {
    client: client ?? undefined,
  });

  const {
    error: ticketEndpointColumnsError,
    data: ticketEndpointColumnsData,
    loading: ticketEndpointColumnsLoading,
  } = useQuery(GET_TICKET_ENDPOINT_COLUMNS, {
    client: client ?? undefined,
    variables: {
      ticket_provider_id: ticketProviderId,
      selected_keys: JSON.stringify(keysObject),
    },
    skip: !ticketManagerMustHaveColumnsMapping,
    onCompleted: (data) => {
      setColumnsSections(data?.endpoint_columns?.fields_sections);
    },
  });

  const [
    getTicketEndpointColumnsValues,
    {
      data: ticketEndpointColumnsValuesData,
      loading: ticketEndpointColumnsValuesLoading,
      error: ticketEndpointColumnsValuesError,
    },
  ] = useLazyQuery(GET_TICKET_ENDPOINT_COLUMNS_VALUES, {
    client: client ?? undefined,
  });

  const openColumnsValuesSections = () => {
    columnsValuesRef?.current?.forEach((ref) => ref?.toggleCollapse?.(true));
  };

  const closeColumnsSection = () => {
    columnsRef?.current?.[0]?.toggleCollapse?.(false);
  };

  useEffect(() => {
    if (!!ticketEndpointColumnsValuesData) {
      setColumnsValuesSections(
        ticketEndpointColumnsValuesData?.field_mapping_definition_fields
          ?.fields_sections
      );
      openColumnsValuesSections();
    }
  }, [ticketEndpointColumnsValuesData]);

  useEffect(() => {
    if (
      !ticketManagerMustHaveColumnsMapping ||
      mode === ticketEndpointColumnsModesEnum.EDIT
    ) {
      getTicketEndpointColumnsValues({
        variables: {
          ticket_provider_id: ticketProviderId,
          selected_keys: JSON.stringify(keysObject),
        },
      });
    }
  }, [ticketManagerMustHaveColumnsMapping, mode]);

  const getField = (
    field: SectionField,
    name: "columns" | "columnsValues"
  ) => {
    return (
      <Controller
        shouldUnregister={false}
        name={`${name}.${field?.name}`}
        render={({
          field: { onChange, onBlur, value },
          fieldState: { isTouched },
        }: {
          field: ControllerRenderProps<FieldValues, string>;
          fieldState: ControllerFieldState;
        }) => {
          return (
            <GenericField
              formState={formState}
              setValue={setValue}
              onChange={onChange}
              description={field?.description}
              name={field?.name}
              registerPrefix={`${name}.${field?.name}`}
              onBlur={onBlur}
              value={value}
              size={field?.size}
              setSingleOption={false}
              error={
                isTouched
                  ? (errors?.[name] as Record<string, unknown> | undefined)?.[
                      field?.name as string
                    ]
                  : null
              }
              mapping={field?.mapping}
              query={field?.query}
              creatable={field?.creatable}
              key={field?.name}
              input={field?.input}
              label={field?.label}
              options={field?.options}
              placeholder={
                field?.label &&
                `Choose${field?.creatable ? " or Create" : ""} ${field?.label}`
              }
            />
          );
        }}
        rules={{
          ...(field?.required && { required: REQUIRED_ERROR_MESSAGE }),
        }}
        defaultValue={field?.defaultValue || null}
        control={control}
      />
    );
  };

  const handleConfirmSection = useCallback(() => {
    onEventTriggered("Confirm_Columns_Mapping");
    closeColumnsSection();
    const selectedColumnsCopy = getColumnsMutationArray(
      selectedColumns
    )?.reduce<Record<string, unknown>>(
      (acc, obj) => ({ ...acc, [obj.key]: obj.value }),
      {}
    );

    getTicketEndpointColumnsValues({
      variables: {
        ticket_provider_id: ticketProviderId,
        selected_keys: JSON.stringify(keysObject),
        selected_columns: JSON.stringify(selectedColumnsCopy),
      },
    });
    openColumnsValuesSections();
  }, [selectedColumns]);

  const shouldDisableConfirmSection = useMemo(
    () => Boolean(errors?.columns || ticketEndpointColumnsValuesLoading),
    [errors?.columns, ticketEndpointColumnsValuesLoading]
  );

  const {
    getSections: getColumnsSections,
    defaultValues: columnsDefaultValues,
  } = useSections(
    columnsSections as RawSection[] | null | undefined,
    setGetPropsFromColumns,
    ((field: SectionField) =>
      getField(field, "columns")) as (field: NormalizedField) => ReactNode,
    true,
    columnsRef,
    true,
    handleConfirmSection,
    shouldDisableConfirmSection
  );

  useEffect(() => {
    if (!!columnsDefaultValues) {
      formReset({ ...getValues(), columns: columnsDefaultValues });
    }
  }, [columnsDefaultValues]);

  const {
    getSections: getColumnsValuesSections,
    defaultValues: columnsValuesDefaultValues,
  } = useSections(
    columnsValuesSections as RawSection[] | null | undefined,
    setGetPropsFromColumnsValues,
    ((field: SectionField) =>
      getField(field, "columnsValues")) as (
      field: NormalizedField
    ) => ReactNode,
    mode === ticketEndpointColumnsModesEnum.CREATE,
    columnsValuesRef
  );
  useEffect(() => {
    if (!!columnsValuesDefaultValues) {
      formReset(
        { ...getValues(), columnsValues: columnsValuesDefaultValues },
        { keepDirty: true }
      );
    }
  }, [columnsValuesDefaultValues]);

  const disableSubmit = useMemo(() => {
    return (
      !isValid ||
      (mode === ticketEndpointColumnsModesEnum.EDIT && !isDirty) ||
      !Object.keys(selectedColumnsValues)?.length ||
      isSubmittingForm ||
      ticketEndpointColumnsLoading ||
      ticketEndpointColumnsValuesLoading
    );
  }, [
    isDirty,
    isSubmittingForm,
    isValid,
    mode,
    selectedColumnsValues,
    ticketEndpointColumnsLoading,
    ticketEndpointColumnsValuesLoading,
  ]);

  const onSubmit = (formData: FieldValues) => {
    setIsSubmittingForm(true);

    saveMapping({
      variables: {
        selected_keys: JSON.stringify(keysObject),
        selected_columns: JSON.stringify(
          getColumnsMutationArray(formData?.columns)?.reduce<
            Record<string, unknown>
          >((acc, obj) => ({ ...acc, [obj.key]: obj.value }), {})
        ),
        ticket_provider_id: ticketProviderId,
        selected_mapping_fields: JSON.stringify(
          getColumnsValuesMutationArray(formData?.columnsValues)?.reduce<
            Record<string, unknown>
          >((acc, obj) => ({ ...acc, [obj.key]: obj.value }), {})
        ),
      },
    })
      .then(() => {
        // keys and columns can be refetched since they're mounted when we submit columns
        // `client` is always provided at runtime (defaulted to null only for the
        // prop signature); mirror the original non-optional access.
        client!
          .refetchQueries({
            include: [
              "getTicketEndpointColumns",
              // columnsValues can be queried with these two variables configurations:
              // 1.  selected_keys and ticket_provider_id
              // 2.  selected_keys, ticket_provider_id and selected_columns
              // the first is used when in edit mode
              // the second is used when confirming columns
              // if we will refetch without variables, then the refetched configuration will be the second one
              // and when we will want to edit mapping again, i.e using the first configuration, the columnsValues will be the last time we were on edit mode - the old ones
              // therefore we have to refetch columnsValues with the first configuration
              // `include` is typed as Array<string | DocumentNode>, but Apollo's
              // refetchQueries also accepts a { query, variables } entry at runtime.
              {
                query: GET_TICKET_ENDPOINT_COLUMNS_VALUES,
                variables: {
                  ticket_provider_id: ticketProviderId,
                  selected_keys: JSON.stringify(keysObject),
                },
              } as unknown as DocumentNode,
            ],
          })
          .then(() => {
            // fields should be evicted from cache and not refetched since the fields query is not mounted when we submit columns
            // and since we want to display the loading state when the fields query is mounted again, we can simply allow it to be refetched and load when mounted.
            client!.cache.evict({ fieldName: "endpoint_fields" });
            // keys should be manipulated directly in the cache instead of refetched since the keys query is not mounted when we submit columns
            // and since we don't want to show loading state when the keys query is mounted again, we can simply manipulate the cache directly.
            const cacheKeysObjectVars = {
              query: GET_TICKET_ENDPOINT_KEYS,
              variables: {
                ticket_provider_id: ticketProviderId,
                selected_keys: JSON.stringify(keysObject),
              },
            };
            const cacheKeysData = client!.cache.readQuery<{
              endpoint_keys?: Record<string, unknown>;
            }>(cacheKeysObjectVars)?.endpoint_keys;
            client!.cache.writeQuery({
              ...cacheKeysObjectVars,
              data: {
                endpoint_keys: { ...cacheKeysData, has_mapping: true },
              },
            });
            onCreateMapping({
              message: "Mapping saved successfully",
            });
            setIsSubmittingForm(false);
            onClose();
          });
      })
      .catch((err: Error) => {
        onCreateMappingError({ message: err.message });
        setIsSubmittingForm(false);
      });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="modal-scroll-area modal-scroll-area-fields-mapping">
          <div className={"modal-text endpoint-mapping-description"}>
            {`In the following sections you will configure the fields, status, and priority/severity mappings between ${systemLabel} and the ticketing system.`}
          </div>
          {!!Object.keys(keysObject)?.length && (
            <ul className={"ticket-mapping-keys-display"}>
              {Object.keys(keysObject).map((key, index) => (
                <li
                  key={`${key}-${index}`}
                  className={"keys-display-item modal-text"}
                >
                  {index === 0 && (
                    <img
                      className={"ticket-manager-logo"}
                      src={getDataSourceIcon(ticketManagerId as string, "S")}
                      alt={ticketManagerFriendlyName}
                    />
                  )}
                  {
                    (getPropsFromKeyField(key) as KeyFieldProps | undefined)
                      ?.options?.find((option) => option.id === keysObject[key])
                      ?.name
                  }
                  {index !== Object.keys(keysObject)?.length - 1 && (
                    <span className={"keys-display-separator"}>/</span>
                  )}
                </li>
              ))}
            </ul>
          )}
          {ticketEndpointColumnsError ? (
            <Loader center subtitle={ticketEndpointColumnsError?.message} />
          ) : ticketEndpointColumnsLoading ? (
            <div className={"tickets-loader-wrap"}>
              <Loader center img={getSvg("icon-spinner")} iconSize={"xxxxl"} />
            </div>
          ) : !!ticketEndpointColumnsData ? (
            getColumnsSections
          ) : (
            <></>
          )}
          {ticketEndpointColumnsValuesError ? (
            <Loader
              center
              subtitle={ticketEndpointColumnsValuesError?.message}
            />
          ) : !!ticketEndpointColumnsValuesData ? (
            getColumnsValuesSections
          ) : (
            <></>
          )}
        </div>

        <ModalButtonsContainer>
          <TextButton
            type="button"
            data-testid={"columns-mapping-cancel"}
            label={"Cancel"}
            onClick={onClose}
          />
          <TextButton
            data-testid={"columns-mapping-submit"}
            type="submit"
            label={"Save & Continue"}
            data-analytics-event="user_click"
            data-analytics-props={`{"name": "save_continue_btn", "container": "edit_mapping_modal"}`}
            onClick={() => {
              onEventTriggered("Save_Continue_Mapping");
            }}
            buttonStyle={"primary"}
            rightIcon={isSubmittingForm ? "seem-icon-spinner spinner" : ""}
            disabled={disableSubmit}
          />
        </ModalButtonsContainer>
      </form>
    </FormProvider>
  );
};

export default TicketEndpointColumns;
